"use client";

import { useState } from "react";

const SESSION_PRICE = 220;
const MIN_SESSIONS = 10;
const DISCOUNTS = { monthly: 0, quarterly: 0.05, annual: 0.1 };
const DISCOUNT_LABELS = { monthly: "Lunar", quarterly: "Trimestrial −5%", annual: "Anual −10%" };

function Slider({
  value, min, max, step, onChange, label, format,
}: {
  value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; label: string; format: (v: number) => string;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[#264653]">{label}</span>
        <span className="text-sm font-semibold text-[#2C969B]">{format(value)}</span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{
            background: `linear-gradient(to right, #2C969B ${pct}%, #2C969B22 ${pct}%)`,
          }}
          className="w-full h-2 rounded-full appearance-none cursor-pointer
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white
            [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#2C969B]
            [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer
            [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5
            [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white
            [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#2C969B]
            [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer
            [&::-moz-range-thumb]:border-none"
        />
      </div>
      <div className="flex justify-between text-[10px] text-[#264653]/40">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}


export default function PriceCalculator() {
  const [sessions, setSessions] = useState(30);
  const [employees, setEmployees] = useState(100);
  const [contract, setContract] = useState<"monthly" | "quarterly" | "annual">("annual");

  const discount = DISCOUNTS[contract];
  // Billed sessions never drop under the monthly minimum.
  const monthlyBase = Math.max(sessions, MIN_SESSIONS) * SESSION_PRICE;
  const monthlyEffective = Math.round(monthlyBase * (1 - discount));
  const perEmployee = Math.round(monthlyEffective / employees);
  const typicalUsage = Math.round(employees * 0.08);

  return (
    <div className="py-20 bg-white border-t border-[#2C969B]/10">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2C969B]/70 mb-3">
            Calculator de cost
          </p>
          <h2 className="text-3xl font-bold text-[#264653]">
            Estimează costul pentru organizația ta
          </h2>
          <p className="text-[#264653]/50 text-sm mt-2 max-w-md mx-auto">
            Calculator orientativ. Prețul final se stabilește în urma unei discuții cu echipa noastră.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* ── INPUTS ── */}
          <div className="space-y-7 bg-[#FDF9F5] rounded-3xl border border-[#E9C46A]/20 p-8">

            <Slider
              label="Ședințe / lună"
              value={sessions}
              min={10}
              max={200}
              step={1}
              onChange={setSessions}
              format={(v) => `${v} ședințe`}
            />
            <p className="text-xs text-[#264653]/40 -mt-4">
              La {employees} angajați, utilizarea tipică (8%) reprezintă ~{typicalUsage} ședințe/lună.
            </p>

            <Slider
              label="Angajați cu acces"
              value={employees}
              min={1}
              max={500}
              step={1}
              onChange={setEmployees}
              format={(v) => `${v} angajați`}
            />

            {/* Contract toggle */}
            <div>
              <p className="text-sm font-medium text-[#264653] mb-3">Durata contract</p>
              <div className="flex gap-2 flex-wrap">
                {(["monthly", "quarterly", "annual"] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setContract(c)}
                    className={`px-4 py-2 rounded-full text-xs font-semibold transition-all border ${
                      contract === c
                        ? "bg-[#2C969B] text-white border-[#2C969B] shadow-sm"
                        : "bg-white text-[#264653]/60 border-[#264653]/15 hover:border-[#2C969B]/40"
                    }`}
                  >
                    {DISCOUNT_LABELS[c]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── OUTPUT ── */}
          <div className="lg:sticky lg:top-8">
            <div className="bg-white rounded-3xl p-8 border border-[#264653]/10 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#264653]/35 mb-6">
                Estimare preț lunar
              </p>

              <div className="space-y-3 mb-5">
                <Row label="Preț / ședință (fără TVA)" value={`${SESSION_PRICE} RON`} />
                <Row label="Ședințe / lună" value={`${sessions}`} />
                <Row label="Cost lunar brut" value={`${monthlyBase.toLocaleString("ro-RO")} RON`} />
                {discount > 0 && (
                  <Row
                    label={`Discount ${contract === "quarterly" ? "trimestrial" : "anual"}`}
                    value={`−${Math.round(monthlyBase * discount).toLocaleString("ro-RO")} RON`}
                    highlight
                  />
                )}
              </div>

              <div className="border-t border-[#264653]/8 pt-5 mb-5">
                <div className="flex justify-between items-end">
                  <span className="text-sm text-[#264653]/50">Cost lunar efectiv</span>
                  <div className="text-right">
                    <p className="text-4xl font-bold text-[#2C969B] leading-none tabular-nums">
                      {monthlyEffective.toLocaleString("ro-RO")}
                    </p>
                    <p className="text-xs text-[#264653]/35 mt-0.5">RON / lună</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#f0f9fa] rounded-2xl p-4 space-y-2.5 mb-6">
                <SmallRow label="Cost per angajat / lună" value={`${perEmployee} RON`} />
                <SmallRow label="Fondul de Terapie" value="10% din profitul nostru" accent />
              </div>

              <p className="text-[10px] text-[#264653]/30 leading-relaxed mb-5">
                Estimare bazată pe distribuție egală între terapeuți. Costul real depinde de preferințele angajaților. Prețul per ședință este fix: 220 RON, fără TVA, cu un minim de 10 ședințe facturate pe lună.
              </p>

              <a
                href="/contact"
                className="block w-full text-center bg-[#DC7050] hover:brightness-110 text-white
                  rounded-full py-3.5 font-semibold text-sm transition-all shadow-md hover:shadow-lg"
              >
                Solicită oferta personalizată
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-sm ${highlight ? "text-[#2C969B]" : "text-[#264653]/50"}`}>{label}</span>
      <span className={`font-semibold tabular-nums ${highlight ? "text-[#2C969B]" : "text-[#264653]"}`}>{value}</span>
    </div>
  );
}

function SmallRow({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-[#264653]/50">{label}</span>
      <span className={`text-sm font-semibold ${accent ? "text-[#2C969B]" : "text-[#264653]"}`}>{value}</span>
    </div>
  );
}
